import { FC, useCallback, useEffect, useMemo } from 'react';
import {
  Controller,
  ResolverOptions,
  ResolverResult,
  useForm,
} from 'react-hook-form';
import { useFetcher, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { parse, isValid, isAfter, isBefore, startOfDay } from 'date-fns';
import { z } from 'zod';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { ResponsiveFormRow } from '@ldt/features/orders';
import { FileUpload } from '@ldt/features/orders/components/file_upload';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { LdtFormActions } from '@ldt/features/orders/components/ldt_form_actions';
import { LdtOrderFormHeader } from '@ldt/features/orders/components/order_form_header';
import { PatientInformationFormSchema } from '@ldt/features/orders/components/patient_information_form_section';
import { PreventLeaveDuringUploadDialog } from '@ldt/features/orders/components/prevent_leave_during_upload_dialog';
import { useLdtFileUploads } from '@ldt/features/orders/hooks/use_ldt_file_uploads';
import { hasMissingTempusClinicalVariables } from '@ldt/features/orders/utils/tempus_clinical_variables';
import { OrderRepository } from 'src/api/order_repository';
import { FormDatePicker } from 'src/components/date_picker';
import { DecimalInput } from 'src/components/decimal_input';
import { SectionHeader } from 'src/components/section_header';
import { SelectList } from 'src/components/select_list';
import {
  processFetcherResponse,
  getProstateBiopsyTStageOptions,
  PROSTATE_BIOPSY_T_STAGES,
  safeParseFloat,
  submitOrderAction,
} from 'src/distribution_platforms/features/order_form';
import {
  useIsLabTech,
  useIsLdtSalesOrCustomerSuccessUser,
} from 'src/hooks/use_auth';
import { useNewOrderSignal } from 'src/hooks/use_new_order_signal';
import {
  LdtOrderDetailFieldsFragment,
  OrderStatus,
  SpecimenType,
  LdtUploadedFileFieldsFragment,
  UploadedFileCategory,
  UploadedFileStatus,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import { WorkflowType } from 'src/types/workflows';
import { notifyError, notifySuccess } from 'src/utils';

const FORM_CONSTANTS = {
  WIDTH: '100%',
  PSA_MIN: 0,
} as const;

/** Hide validation once at least one file exists for a required upload category. */
function getRequiredFileValidationText(
  files: LdtUploadedFileFieldsFragment[],
  errorMessage?: string,
): string | undefined {
  return files.length > 0 ? undefined : errorMessage;
}

// Type for form data - inferred from the schema created with default values
export type LdtMetastaticProstateTempusFormData = z.infer<
  ReturnType<typeof createLdtMetastaticProstateTempusFormSchema>
>;

export const createLdtMetastaticProstateTempusFormSchema = (
  requireDraftDocuments = false,
) => {
  const requiredFileSchema = (label: string) =>
    z
      .instanceof(File, { message: `${label} is required` })
      .nullable()
      .refine((file) => file !== null, { message: `${label} is required` });

  const optionalFileSchema = z.instanceof(File).nullable().optional();

  return z
    .object({
      // Patient Information
      ...PatientInformationFormSchema.shape,

      // Draft documents — required for lab tech while order is in Draft
      pathologyReport: requireDraftDocuments
        ? requiredFileSchema('Pathology Report')
        : optionalFileSchema,
      testRequisitionForm: requireDraftDocuments
        ? requiredFileSchema('Test Requisition Form')
        : optionalFileSchema,

      // Clinical Information
      baselinePsa: z
        .number()
        .min(
          FORM_CONSTANTS.PSA_MIN,
          `PSA must be at least ${FORM_CONSTANTS.PSA_MIN} ng/mL`,
        )
        .nullable(),
      tStage: z.enum(PROSTATE_BIOPSY_T_STAGES).nullable(),

      // Specimen Information
      dateOfProcedure: z
        .string()
        .optional()
        .refine(
          (val) => {
            if (!val) return true;
            try {
              const date = parse(val, 'yyyy-MM-dd', new Date());
              if (!isValid(date)) return false;

              const minDate = new Date(1900, 0, 1); // January 1, 1900
              const procedureDay = startOfDay(date);
              const today = startOfDay(new Date());
              return isAfter(date, minDate) && !isAfter(procedureDay, today);
            } catch {
              return false;
            }
          },
          {
            message:
              'Date of Procedure must be after 1900 and cannot be in the future',
          },
        ),
    })
    .superRefine((data, ctx) => {
      if (!data.dateOfProcedure || !data.dateOfBirth) return;

      const dateOfBirth = parse(data.dateOfBirth, 'yyyy-MM-dd', new Date());
      const dateOfProcedure = parse(
        data.dateOfProcedure,
        'yyyy-MM-dd',
        new Date(),
      );
      if (!isValid(dateOfBirth) || !isValid(dateOfProcedure)) return;

      if (isBefore(startOfDay(dateOfProcedure), startOfDay(dateOfBirth))) {
        ctx.addIssue({
          code: 'custom',
          message: 'Date of Procedure cannot be before Date of Birth',
          path: ['dateOfProcedure'],
        });
      }
    });
};

export type LdtMetastaticProstateTempusWarnings = Partial<
  Record<keyof LdtMetastaticProstateTempusFormData, string>
>;

export const createMetastaticProstateTempusCustomResolver =
  (requireClinicalFields = false, requireDraftDocuments = false) =>
  async (
    data: LdtMetastaticProstateTempusFormData,
    context: unknown,
    options: ResolverOptions<LdtMetastaticProstateTempusFormData>,
  ): Promise<
    ResolverResult<LdtMetastaticProstateTempusFormData> & {
      warnings: LdtMetastaticProstateTempusWarnings;
    }
  > => {
    const schema = createLdtMetastaticProstateTempusFormSchema(
      requireDraftDocuments,
    );
    const zodResult = await zodResolver(schema)(data, context, options);

    const warnings: LdtMetastaticProstateTempusWarnings = {};
    const extraErrors: Record<string, { type: string; message: string }> = {};

    const isPsaMissing = !data.baselinePsa && data.baselinePsa !== 0;
    if (isPsaMissing) {
      if (requireClinicalFields) {
        extraErrors.baselinePsa = {
          type: 'required',
          message: 'Most Recent Pre-Treatment PSA is required',
        };
      } else {
        warnings.baselinePsa = 'Most Recent Pre-Treatment PSA is missing';
      }
    }

    if (!data.tStage) {
      if (requireClinicalFields) {
        extraErrors.tStage = {
          type: 'required',
          message: 'Clinical T-stage is required',
        };
      } else {
        warnings.tStage = 'Clinical T-stage is missing';
      }
    }

    if (!data.dateOfProcedure) {
      if (requireClinicalFields) {
        extraErrors.dateOfProcedure = {
          type: 'required',
          message: 'Date of Procedure is required',
        };
      } else {
        warnings.dateOfProcedure = 'Date of Procedure is missing';
      }
    }

    return {
      values: zodResult.values,
      errors: { ...zodResult.errors, ...extraErrors },
      warnings,
    } as ResolverResult<LdtMetastaticProstateTempusFormData> & {
      warnings: LdtMetastaticProstateTempusWarnings;
    };
  };

// Helper function to create default form values
export const createMetastaticProstateTempusDefaultFormValues = (
  initialOrder: LdtOrderDetailFieldsFragment,
): LdtMetastaticProstateTempusFormData => {
  let pathologyReport: File | null = null;
  let testRequisitionForm: File | null = null;
  if (initialOrder.uploadedFiles?.edges) {
    const pathologyReportFile = initialOrder.uploadedFiles.edges.find(
      (edge) =>
        edge?.node?.category === UploadedFileCategory.PathologyReport &&
        edge.node.status === UploadedFileStatus.Complete,
    );
    if (pathologyReportFile?.node) {
      pathologyReport = new File([], pathologyReportFile.node.filename);
    }

    const trfFile = initialOrder.uploadedFiles.edges.find(
      (edge) =>
        edge?.node?.category === UploadedFileCategory.TestRequisitionForm &&
        edge.node.status === UploadedFileStatus.Complete,
    );
    if (trfFile?.node) {
      testRequisitionForm = new File([], trfFile.node.filename);
    }
  }

  return {
    firstName: initialOrder.patient?.firstNamePhi ?? '',
    lastName: initialOrder.patient?.lastNamePhi ?? '',
    dateOfBirth: initialOrder.patient?.dateOfBirthPhi ?? '',
    baselinePsa: initialOrder.psa ?? null,
    tStage: initialOrder.tStage ?? null,
    dateOfProcedure: initialOrder.labProcedureDatePhi ?? undefined,
    pathologyReport,
    testRequisitionForm,
  };
};

export const LdtMetastaticProstateTempusOrderForm: FC<{
  orderRepository: OrderRepository;
  initialOrder: LdtOrderDetailFieldsFragment;
  onClose: () => void;
  workflowType: WorkflowType;
}> = ({ orderRepository, initialOrder, onClose, workflowType }) => {
  const isCSOrSalesUser = useIsLdtSalesOrCustomerSuccessUser();
  const isLabTechUser = useIsLabTech();
  const fetcher = useFetcher();
  const location = useLocation();
  const ldtOrderRepository = orderRepository as LdtOrderRepository;

  const isEditingSubmittedOrder =
    isCSOrSalesUser &&
    initialOrder.status === OrderStatus.QualityControlPending;

  const isEditingQcApprovedTempusOrder =
    workflowType === WorkflowType.TEMPUS &&
    initialOrder.status === OrderStatus.QualityControlApproved &&
    (isLabTechUser || isCSOrSalesUser);

  const showClinicalSections =
    isEditingQcApprovedTempusOrder ||
    (isCSOrSalesUser &&
      initialOrder.status === OrderStatus.QualityControlPending);

  const isDraftOrder = initialOrder.status === OrderStatus.Draft;
  const canEditDraftDocuments =
    (isLabTechUser || isCSOrSalesUser) && isDraftOrder;
  const requireDraftDocuments = canEditDraftDocuments;

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    watch,
  } = useForm<LdtMetastaticProstateTempusFormData>({
    resolver: createMetastaticProstateTempusCustomResolver(
      isEditingQcApprovedTempusOrder,
      requireDraftDocuments,
    ),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues:
      createMetastaticProstateTempusDefaultFormValues(initialOrder),
  });

  // Use the custom hook for file upload management
  const {
    getFilesForCategory,
    handleFileSelected,
    handleFileRemoved,
    isFileDeleting,
  } = useLdtFileUploads({
    order: initialOrder,
    orderRepository: ldtOrderRepository,
    onFileCategoryUpdate: useCallback(
      (
        category: UploadedFileCategory,
        file?: LdtUploadedFileFieldsFragment,
        fileObject?: File,
      ) => {
        // Update form value for Pathology Report if a file was added or removed
        if (category === UploadedFileCategory.PathologyReport) {
          if (file) {
            const fileObj = fileObject || new File([], file.filename);
            setValue('pathologyReport', fileObj, {
              shouldValidate: true,
            });
          } else {
            setValue('pathologyReport', null, {
              shouldValidate: true,
            });
          }
        }
        if (category === UploadedFileCategory.TestRequisitionForm) {
          setValue(
            'testRequisitionForm',
            file ? fileObject || new File([], file.filename) : null,
            { shouldValidate: true },
          );
        }
      },
      [setValue],
    ),
    onError: notifyError,
  });

  // Memoize files for each category - recalculates when uploads change to show progress updates
  const pathologyReportFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.PathologyReport),
    [getFilesForCategory],
  );

  const trfFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.TestRequisitionForm),
    [getFilesForCategory],
  );

  const otherFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.Other),
    [getFilesForCategory],
  );

  const fileUploadConfigs = useMemo(() => {
    const configs: Array<{
      label: string;
      category: UploadedFileCategory;
      required?: boolean;
      helperText?: string;
      accept?: string;
      allowMultiple?: boolean;
      files: LdtUploadedFileFieldsFragment[];
      validationText?: string;
      showDivider: (index: number, total: number) => boolean;
      visible: boolean;
    }> = [
      {
        label: 'Test Requisition Form',
        category: UploadedFileCategory.TestRequisitionForm,
        required: true,
        helperText: 'only one PDF file is allowed',
        accept: '.pdf',
        allowMultiple: false,
        files: trfFiles,
        validationText: getRequiredFileValidationText(
          trfFiles,
          errors.testRequisitionForm?.message,
        ),
        showDivider: () => false,
        visible: true,
      },
      {
        label: 'Pathology Report',
        category: UploadedFileCategory.PathologyReport,
        required: true,
        files: pathologyReportFiles,
        validationText: getRequiredFileValidationText(
          pathologyReportFiles,
          errors.pathologyReport?.message,
        ),
        showDivider: () => false,
        visible: true,
      },
      {
        label: 'Other(s)',
        category: UploadedFileCategory.Other,
        files: otherFiles,
        showDivider: () => false,
        visible: true,
      },
    ];

    // Filter out invisible configs and return only visible ones
    return configs.filter((config) => config.visible);
  }, [
    pathologyReportFiles,
    trfFiles,
    otherFiles,
    errors.pathologyReport?.message,
    errors.testRequisitionForm?.message,
  ]);

  // Convert form data to Order object
  const getOrderFromFormData = useCallback((): LdtOrderDetailFieldsFragment => {
    const formData = getValues();

    // Exclude uploadedFiles from initialOrder spread since it's managed separately
    // via cancelUpload/completeUpload and should be preserved from the persisted order
    const { uploadedFiles: _uploadedFiles, ...orderWithoutFiles } =
      initialOrder;

    return {
      ...orderWithoutFiles,
      patient: {
        ...initialOrder.patient,
        firstNamePhi: formData.firstName?.trim() || null,
        lastNamePhi: formData.lastName?.trim() || null,
        dateOfBirthPhi: formData.dateOfBirth || null,
      },
      psa:
        typeof formData.baselinePsa === 'string'
          ? safeParseFloat(formData.baselinePsa)
          : formData.baselinePsa,
      tStage: formData.tStage,
      labProcedureDatePhi: formData.dateOfProcedure || null,
      specimenType: SpecimenType.MetastaticProstateBiopsy,
      status: initialOrder.status,
    } as LdtOrderDetailFieldsFragment;
  }, [getValues, initialOrder]);

  // Handle action results
  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
        if (result.closeDialog) {
          onClose();
        }
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state, onClose]);

  const isNewOrder = useNewOrderSignal(initialOrder.id);

  // Trigger validation right when the form opens if this is an existing order.
  useEffect(() => {
    if (!isNewOrder) {
      trigger();
    }
  }, [isNewOrder, trigger]);

  const handleSaveOrder = async (
    _qcRevisionReason?: string,
    revertStatus?: OrderStatus,
    tempusSaveOptions?: {
      sendTempusMissingClinicalVariablesEmail?: boolean;
    },
  ) => {
    const orderData = getOrderFromFormData();
    const hasMissingClinicalVars = hasMissingTempusClinicalVariables({
      psa: orderData.psa,
      tStage: orderData.tStage,
      labProcedureDatePhi: orderData.labProcedureDatePhi,
    });
    const ldtUpdateOptions =
      hasMissingClinicalVars &&
      tempusSaveOptions?.sendTempusMissingClinicalVariablesEmail !== undefined
        ? {
            sendTempusMissingClinicalVariablesEmail:
              tempusSaveOptions.sendTempusMissingClinicalVariablesEmail,
          }
        : undefined;

    if (revertStatus) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.UPDATE_REJECTED_ORDER,
        location,
        { ...orderData, status: revertStatus },
      );
    } else if (!orderData.id) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.CREATE,
        location,
        orderData,
      );
    } else {
      submitOrderAction(
        fetcher.submit,
        OrderAction.UPDATE,
        location,
        orderData,
        ldtUpdateOptions,
      );
    }
  };

  const handleSubmitOrder = async () => {
    const orderData = getOrderFromFormData();

    // Submit draft orders; otherwise, update the order.
    const action =
      orderData.status === OrderStatus.Draft
        ? OrderAction.SUBMIT
        : OrderAction.UPDATE;

    submitOrderAction(fetcher.submit, action, location, orderData);
  };

  const handleDiscard = async () => {
    // Unsaved orders do not need to be deleted because they don't exist yet
    if (isDraftOrder && !isNewOrder) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.DELETE,
        location,
        undefined,
      );
    } else {
      notifySuccess('Order discarded successfully.');
      onClose();
    }
  };
  const hasAiReport = initialOrder.hasAiReport ?? false;
  const disablePatientInformationFields = !(
    isCSOrSalesUser && initialOrder.status === OrderStatus.QualityControlPending
  );
  const baselinePsa = watch('baselinePsa');
  const tStage = watch('tStage');
  const dateOfProcedure = watch('dateOfProcedure');
  const hasMissingClinicalVariables =
    isEditingSubmittedOrder &&
    hasMissingTempusClinicalVariables({
      psa:
        typeof baselinePsa === 'string'
          ? safeParseFloat(baselinePsa)
          : baselinePsa,
      tStage,
      labProcedureDatePhi: dateOfProcedure,
    });

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(handleSubmitOrder)}
      noValidate
      sx={{
        width: '100%',
        height: '100%',
        maxWidth: '2000px',
        maxHeight: '1500px',
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        px: 2,
      }}
    >
      {/* Header */}
      <LdtOrderFormHeader
        title={initialOrder.crossOrgId ? 'Edit Order' : 'Create Order'}
        specimenType={SpecimenType.MetastaticProstateBiopsy}
        onClose={onClose}
        crossOrgId={initialOrder.crossOrgId}
        externalId={initialOrder.externalId}
      />
      <Typography
        variant='caption'
        sx={{
          color: 'text.secondary',
        }}
      >
        *Indicates a required field
      </Typography>
      {/* Scrollable content area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          mt: 2,
        }}
      >
        {/* Patient Information Section */}
        {[OrderStatus.Draft, OrderStatus.QualityControlPending].includes(
          initialOrder.status,
        ) && (
          <FormSectionCard
            header={<SectionHeader title='Patient Information' />}
            sx={{ borderBottom: 'none' }}
          >
            <Box
              data-testid='patient-information-form-section'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <ResponsiveFormRow>
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name={'firstName'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        fullWidth
                        disabled={disablePatientInformationFields}
                        label='First Name'
                        data-testid='patient-information-first-name'
                        error={!!errors.firstName}
                        helperText={
                          errors.firstName?.message?.toString() ?? ' '
                        }
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name={'lastName'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        fullWidth
                        disabled={disablePatientInformationFields}
                        label='Last Name'
                        data-testid='patient-information-last-name'
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message?.toString() ?? ' '}
                      />
                    )}
                  />
                </Box>
              </ResponsiveFormRow>
              <ResponsiveFormRow>
                <Box
                  data-testid='patient-information-date-of-birth'
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name={'dateOfBirth'}
                    control={control}
                    render={({ field }) => (
                      <FormDatePicker
                        label='Date of Birth'
                        maxDate={new Date()}
                        value={field.value}
                        required
                        fullWidth
                        disabled={disablePatientInformationFields}
                        error={!!errors.dateOfBirth}
                        helperText={
                          errors.dateOfBirth?.message?.toString() ?? ' '
                        }
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                  }}
                />
              </ResponsiveFormRow>
            </Box>
          </FormSectionCard>
        )}

        {/* Upload Document Section */}
        {canEditDraftDocuments && (
          <FormSectionCard
            header={
              <SectionHeader
                title='Upload Document'
                subtitle='pdf, png, jpeg types are supported'
              />
            }
            sx={{
              borderBottom: showClinicalSections ? 'none' : '1px solid #e0e0e0',
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                mb: 1,
              }}
            >
              {fileUploadConfigs.map((config, index) => (
                <Grid key={config.category} size={{ xs: 12, sm: 6, lg: 3 }}>
                  <FileUpload
                    label={config.label}
                    category={config.category}
                    required={config.required}
                    helperText={config.helperText}
                    isFileDeleting={isFileDeleting}
                    validationText={config.validationText}
                    onFileSelected={handleFileSelected}
                    onFileRemoved={handleFileRemoved}
                    accept={config.accept}
                    allowMultiple={config.allowMultiple}
                    showDivider={config.showDivider(
                      index,
                      fileUploadConfigs.length,
                    )}
                    files={config.files}
                  />
                </Grid>
              ))}
            </Grid>
          </FormSectionCard>
        )}

        {/* Clinical Information Section */}
        {showClinicalSections && (
          <FormSectionCard
            header={<SectionHeader title='Clinical Information' />}
            sx={{ borderBottom: 'none' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {/* PSA and T-Stage Row */}
              <ResponsiveFormRow>
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name='baselinePsa'
                    control={control}
                    render={({ field }) => {
                      const psaValue = field.value;
                      const hasError = !!errors.baselinePsa;
                      const isMissingPsa = psaValue === null;
                      const warningText = isMissingPsa
                        ? 'Most recent pre-treatment PSA is missing'
                        : '';
                      const isPsaLocked =
                        isEditingQcApprovedTempusOrder &&
                        initialOrder.psa !== null &&
                        initialOrder.psa !== undefined;

                      return (
                        <DecimalInput
                          fullWidth
                          required={isEditingQcApprovedTempusOrder}
                          label='Most Recent Pre-Treatment PSA (ng/mL)'
                          value={psaValue}
                          decimalPlaces={2}
                          min={FORM_CONSTANTS.PSA_MIN}
                          // Nobody knows the maximum PSA level for metastatic prostate biopsy yet.
                          // max={FORM_CONSTANTS.MAX_PSA}
                          disabled={isPsaLocked}
                          error={hasError}
                          validationText={errors.baselinePsa?.message}
                          warningText={warningText}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                        />
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name={'tStage'}
                    control={control}
                    render={({ field }) => {
                      const isTStageLocked =
                        isEditingQcApprovedTempusOrder && !!initialOrder.tStage;

                      return (
                        <SelectList
                          {...field}
                          label='Clinical T-stage'
                          labelId='t-stage-label'
                          value={field.value ?? undefined}
                          required={isEditingQcApprovedTempusOrder}
                          disabled={isTStageLocked}
                          onChange={(event) => {
                            field.onChange(event);
                            void trigger('tStage');
                          }}
                          onBlur={field.onBlur}
                          options={getProstateBiopsyTStageOptions(false, false)}
                          validationText={
                            errors.tStage?.message?.toString() ?? ''
                          }
                          warningText={
                            !field.value ? 'Clinical T-stage is missing' : ''
                          }
                        />
                      );
                    }}
                  />
                </Box>
              </ResponsiveFormRow>
            </Box>
          </FormSectionCard>
        )}

        {/* Specimen Information Section */}
        {showClinicalSections && (
          <FormSectionCard
            header={<SectionHeader title='Specimen Information' />}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <ResponsiveFormRow>
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name={'dateOfProcedure'}
                    control={control}
                    render={({ field }) => {
                      const isProcedureDateLocked =
                        isEditingQcApprovedTempusOrder &&
                        !!initialOrder.labProcedureDatePhi;

                      return (
                        <FormDatePicker
                          label='Date of Procedure'
                          maxDate={new Date()}
                          value={field.value}
                          fullWidth
                          required={isEditingQcApprovedTempusOrder}
                          disabled={isProcedureDateLocked}
                          error={!!errors.dateOfProcedure}
                          helperText={
                            errors.dateOfProcedure?.message?.toString() ?? ' '
                          }
                          warningText={
                            !field.value ? 'Date of Procedure is missing' : ''
                          }
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                  }}
                />
              </ResponsiveFormRow>
            </Box>
          </FormSectionCard>
        )}
      </Box>
      {/* Fixed FormActions at the bottom */}
      <Box
        sx={{
          flexShrink: 0,
          mt: 2,
        }}
      >
        <LdtFormActions
          showSubmitButton={!isEditingSubmittedOrder}
          showSaveButton={isEditingSubmittedOrder}
          showDraftActionButtons={false}
          isSubmitting={fetcher.state !== 'idle'}
          onValidateBeforeSubmit={async () => trigger()}
          onValidateBeforeSave={async () => trigger()}
          onConfirmSubmit={handleSubmitOrder}
          onSaveAsDraft={handleSaveOrder}
          onDiscard={handleDiscard}
          requireDiscardConfirmation={!isNewOrder}
          onDiscardSuccess={() => {
            notifySuccess('Order discarded successfully.');
            onClose();
          }}
          showRevisionReasonDialog={false}
          showSaveWithReportDialog={hasAiReport && isCSOrSalesUser}
          tempusCsSaveConfig={
            isEditingSubmittedOrder
              ? { hasMissingClinicalVariables }
              : undefined
          }
          onConfirmSave={(qcRevisionReason, options) =>
            handleSaveOrder(qcRevisionReason, undefined, options)
          }
          onConfirmSaveWithReport={(revertStatus) =>
            handleSaveOrder(undefined, revertStatus)
          }
          onFileSelected={handleFileSelected}
          onFileRemoved={handleFileRemoved}
          isFileDeleting={isFileDeleting}
          existingQcRevisionReason={initialOrder.qcRevisionReason}
        />
      </Box>

      <PreventLeaveDuringUploadDialog orderId={initialOrder.id} />
    </Box>
  );
};
